import React, { createContext, useContext, useCallback } from 'react';
import { useMutation } from '@edreamsodigeo/frontend-api-client';
import { CREATE_SHOPPING_BUNDLE } from '../operations/createShoppingBundle';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  useFlow,
  useRouter,
  Desktop,
  Mobile,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import {
  FECO_CHECKOUT_PASSENGERS_MOBILE,
  FECO_CHECKOUT_PASSENGERS_DESKTOP,
} from 'clientState/ab/aliases';
import {
  ShoppingType,
  CreateShoppingBundleMutation,
  CreateShoppingBundleMutationVariables,
} from 'generated/graphql';

const INITIAL_ACTIONS = {
  goToPassengers: () => {},
  createShoppingBundle: (_itineraryInfoForCreateShoppingCart) => {},
};

const CreateShoppingBundleActionsContext = createContext(INITIAL_ACTIONS);
const noop = () => {};

export const CreateShoppingBundleProvider = ({ children }) => {
  const { push } = useRouter();
  const flow = useFlow();
  const navigateToPassengers = useCallback(
    ({ data }) => {
      const shoppingId = data.createShoppingBundle.shoppingBundle.shoppingId;
      push(`checkout_passengers/${shoppingId}/`);
    },
    [push]
  );
  const createShoppingBundleMutation = useMutation<
    CreateShoppingBundleMutation,
    CreateShoppingBundleMutationVariables
  >(CREATE_SHOPPING_BUNDLE, {
    onSuccess: flow === 'desktop' ? navigateToPassengers : noop,
  });
  const { errors, data, mutate } = createShoppingBundleMutation;
  const goToPassengers = useCallback(() => {
    navigateToPassengers({ data });
  }, [navigateToPassengers, data]);

  const createShoppingBundle = useCallback(
    (itineraryInfoForCreateShoppingCart) => {
      if (itineraryInfoForCreateShoppingCart) {
        const { request } = itineraryInfoForCreateShoppingCart;
        mutate({
          request: {
            extension: request.extensions,
            fareItineraryKey: request.fareItineraryKey,
            preview: false,
            promotedItineraryTypes: request.promotedItineraryTypes,
            resident: false,
            searchId: `${request.searchId}`,
            segmentKey: request.segmentKeys,
            shoppingType: ShoppingType.Cart,
          },
        });
      }
    },
    [mutate]
  );

  const actions = {
    goToPassengers,
    createShoppingBundle,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={errors}
        id="CreateShoppingBundleProvider"
      />
      <CreateShoppingBundleActionsContext.Provider value={actions}>
        {children}
      </CreateShoppingBundleActionsContext.Provider>
    </>
  );
};

const RenderChildren = ({ children }) => {
  return <>{children}</>;
};

export const ShoppingBundleWrapperMobile = createFeature({
  name: FECO_CHECKOUT_PASSENGERS_MOBILE,
  variations: [RenderChildren, RenderChildren],
  version: 1,
});

// CreateShoppingBundleProvider should be called in the variations, this is just a quick fix to call the OF1 createShoppingCart endpoint + IP rules.
// As we do not return the provider, const { createShoppingBundle } = useCreateShoppingBundleActions() || {}; will return an empty function that will not do anything
// and will send the control to HTML5 in selectItineraryAndContinueToPassengers(itinerarySelection);
// A proper fix is needed to call the createShoppingBundle mutation after the IP rules are checked
export const ShoppingBundleWrapperDesktop = createFeature({
  name: FECO_CHECKOUT_PASSENGERS_DESKTOP,
  variations: [RenderChildren, RenderChildren],
  version: 1,
});

export const CreateShoppingBundleProviderWrapper = ({ children }) => {
  return (
    <>
      <Desktop>
        <ShoppingBundleWrapperDesktop>{children}</ShoppingBundleWrapperDesktop>
      </Desktop>
      <Mobile>
        <ShoppingBundleWrapperMobile>{children}</ShoppingBundleWrapperMobile>
      </Mobile>
    </>
  );
};

export const useCreateShoppingBundleActions = () =>
  useContext(CreateShoppingBundleActionsContext);
